import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, User, Lock, Mail, Shield, Camera, Trash2, Eye, EyeOff, Check, X, Loader2, Key, Wifi
} from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("profile");

  // Profile state
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email state
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // 2FA state
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);

  // API Key state
  const [apiKey, setApiKey] = useState("");
  const [apiKeyActive, setApiKeyActive] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [savedApiKey, setSavedApiKey] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAvatar(user.avatar || null);
      setTwoFAEnabled(user.totp_enabled || false);
      // Load saved API key
      const saved = localStorage.getItem(`api_key_${user.id}`);
      if (saved) { setSavedApiKey(saved); setApiKeyActive(true); }
    }
  }, [user]);

  const tabs = [
    { id: "profile", label: "Профиль", icon: User },
    { id: "password", label: "Пароль", icon: Lock },
    { id: "email", label: "Почта", icon: Mail },
    { id: "2fa", label: "Authenticator", icon: Shield },
    { id: "api", label: "API Ключ", icon: Key },
  ];

  // Profile handlers
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Выберите изображение");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Файл слишком большой (макс. 2MB)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setProfileLoading(true);
    try {
      const res = await axios.post(`${API}/settings/avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setAvatar(res.data.avatar);
      await refreshUser();
      toast.success("Аватар загружен");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка загрузки");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    setProfileLoading(true);
    try {
      await axios.delete(`${API}/settings/avatar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvatar(null);
      await refreshUser();
      toast.success("Аватар удалён");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSave = async () => {
    if (!name.trim()) {
      toast.error("Введите имя");
      return;
    }

    setProfileLoading(true);
    try {
      await axios.put(
        `${API}/settings/profile`,
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success("Профиль обновлён");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка обновления");
    } finally {
      setProfileLoading(false);
    }
  };

  // Password handlers
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Заполните все поля");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Пароли не совпадают");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Пароль должен быть не менее 6 символов");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(
        `${API}/settings/password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Пароль изменён");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка смены пароля");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Email handlers
  const handleEmailChange = async () => {
    if (!newEmail || !emailPassword) {
      toast.error("Заполните все поля");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error("Введите корректный email");
      return;
    }

    setEmailLoading(true);
    try {
      await axios.put(
        `${API}/settings/email`,
        {
          new_email: newEmail,
          password: emailPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success("Email изменён");
      setNewEmail("");
      setEmailPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка смены email");
    } finally {
      setEmailLoading(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const res = await axios.post(
        `${API}/settings/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setSetupMode(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка настройки 2FA");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (otpCode.length !== 6) {
      toast.error("Введите 6-значный код");
      return;
    }

    setTwoFALoading(true);
    try {
      await axios.post(
        `${API}/settings/2fa/verify`,
        {
          code: otpCode,
          secret: secret,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      setTwoFAEnabled(true);
      setSetupMode(false);
      setQrCode(null);
      setSecret(null);
      setOtpCode("");
      toast.success("2FA включена!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Неверный код");
      setOtpCode("");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error("Введите пароль");
      return;
    }

    setTwoFALoading(true);
    try {
      await axios.post(
        `${API}/settings/2fa/disable`,
        { password: disablePassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      setTwoFAEnabled(false);
      setShowDisableModal(false);
      setDisablePassword("");
      toast.success("2FA отключена");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Неверный пароль");
    } finally {
      setTwoFALoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bybit-grid-subtle">
      {/* Header */}
      <header className="border-b border-[#1F1F1F] bg-[#0A0A0C]/80 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-white">Настройки</h1>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? "bg-[#FF8A1E] text-white font-bold"
                    : "bg-[#0A0A0C] text-[#A1A1AA] hover:text-white border border-[#1F1F1F]"
                }`}
              >
                <Icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-6">
            <h2 className="text-lg font-bold text-white mb-6">Редактирование профиля</h2>

            {/* Avatar */}
            <div className="flex items-center gap-6 mb-8">
              <div className="relative">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Avatar"
                    className="w-24 h-24 rounded-full object-cover border-2 border-[#1F1F1F]"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold text-[#EDEDED] border-2 border-[#1F1F1F]">
                    {getInitials(user?.name)}
                  </div>
                )}
                <button
                  onClick={handleAvatarClick}
                  disabled={profileLoading}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#FF8A1E] rounded-full flex items-center justify-center text-white hover:bg-[#E5E5E5] transition-colors"
                >
                  <Camera size={16} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <div>
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-[#A1A1AA] text-sm">{user?.email}</p>
                {avatar && (
                  <button
                    onClick={handleDeleteAvatar}
                    disabled={profileLoading}
                    className="mt-2 flex items-center gap-1 text-[#F87171] text-sm hover:underline"
                  >
                    <Trash2 size={14} />
                    Удалить фото
                  </button>
                )}
              </div>
            </div>

            {/* Name */}
            <div className="mb-6">
              <label className="block text-[#A1A1AA] text-sm mb-2">Имя</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                placeholder="Введите имя"
              />
            </div>

            {/* ID Connect (read-only) */}
            <div className="mb-6">
              <label className="block text-[#A1A1AA] text-sm mb-2">ID Connect</label>
              <div
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-[#FF8A1E] font-mono flex items-center justify-between"
                data-testid="settings-bybitid"
              >
                <span>{user?.bybit_id || "-"}</span>
                <span className="text-[#71717A] text-xs">привязан</span>
              </div>
            </div>

            <button
              onClick={handleProfileSave}
              disabled={profileLoading}
              className="flex items-center gap-2 bg-[#FF8A1E] hover:bg-[#E67A00] text-white font-bold px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              {profileLoading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              Сохранить
            </button>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === "password" && (
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-6">
            <h2 className="text-lg font-bold text-white mb-6">Смена пароля</h2>

            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Текущий пароль</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Новый пароль</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Подтвердите пароль</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                  placeholder="••••••••"
                />
              </div>

              <button
                onClick={handlePasswordChange}
                disabled={passwordLoading}
                className={`flex items-center gap-2 font-bold px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 mt-6 ${
                  currentPassword && newPassword && confirmPassword && newPassword === confirmPassword && newPassword.length >= 6
                    ? 'bg-[#FF8A1E] hover:bg-[#E67A00] text-white cursor-pointer' 
                    : 'bg-[#1F1F1F] text-[#71717A] cursor-not-allowed'
                }`}
              >
                {passwordLoading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />}
                Изменить пароль
              </button>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === "email" && (
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-6">
            <h2 className="text-lg font-bold text-white mb-6">Смена почты</h2>

            <div className="mb-6 p-4 bg-[#000000] rounded-lg border border-[#1F1F1F]">
              <p className="text-[#A1A1AA] text-sm">Текущий email</p>
              <p className="text-white font-medium">{user?.email}</p>
            </div>

            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Новый email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                  placeholder="new@email.com"
                />
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Пароль для подтверждения</label>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                  placeholder="••••••••"
                />
              </div>

              <button
                onClick={handleEmailChange}
                disabled={emailLoading}
                className={`flex items-center gap-2 font-bold px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 mt-6 ${
                  newEmail && emailPassword && newEmail.includes('@')
                    ? 'bg-[#FF8A1E] hover:bg-[#E67A00] text-white cursor-pointer' 
                    : 'bg-[#1F1F1F] text-[#71717A] cursor-not-allowed'
                }`}
              >
                {emailLoading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                Изменить email
              </button>
            </div>
          </div>
        )}

        {/* 2FA Tab */}
        {activeTab === "2fa" && (
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-6">
            <h2 className="text-lg font-bold text-white mb-2">Google Authenticator</h2>
            <p className="text-[#A1A1AA] text-sm mb-6">
              Двухфакторная аутентификация добавляет дополнительный уровень безопасности к вашему аккаунту
            </p>

            {/* Status */}
            <div className="mb-6 p-4 bg-[#000000] rounded-lg border border-[#1F1F1F] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${twoFAEnabled ? "bg-[#34D399]" : "bg-[#A1A1AA]"}`} />
                <span className="text-white">
                  {twoFAEnabled ? "Включена" : "Отключена"}
                </span>
              </div>
              {twoFAEnabled && (
                <button
                  onClick={() => setShowDisableModal(true)}
                  className="text-[#F87171] text-sm hover:underline"
                >
                  Отключить
                </button>
              )}
            </div>

            {/* Setup Mode */}
            {!twoFAEnabled && !setupMode && (
              <button
                onClick={handleSetup2FA}
                disabled={twoFALoading}
                className="flex items-center gap-2 bg-[#FF8A1E] hover:bg-[#E67A00] text-white font-bold px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                {twoFALoading ? <Loader2 size={18} className="animate-spin" /> : <Shield size={18} />}
                Настроить 2FA
              </button>
            )}

            {/* QR Code and Verification */}
            {setupMode && qrCode && (
              <div className="space-y-6">
                <div className="p-6 bg-[#000000] rounded-lg border border-[#1F1F1F]">
                  <h3 className="text-white font-medium mb-4">
                    1. Отсканируйте QR-код в приложении Google Authenticator
                  </h3>
                  <div className="flex justify-center mb-4">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48 rounded-lg" />
                  </div>
                  <div className="text-center">
                    <p className="text-[#A1A1AA] text-sm mb-2">Или введите код вручную:</p>
                    <code className="bg-[#0A0A0C] text-[#EDEDED] px-4 py-2 rounded font-mono text-sm break-all">
                      {secret}
                    </code>
                  </div>
                </div>

                <div className="p-6 bg-[#000000] rounded-lg border border-[#1F1F1F]">
                  <h3 className="text-white font-medium mb-4">
                    2. Введите 6-значный код из приложения
                  </h3>
                  <div className="flex flex-col items-center gap-4">
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={(value) => setOtpCode(value)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                        <InputOTPSlot index={1} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                        <InputOTPSlot index={2} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                        <InputOTPSlot index={3} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                        <InputOTPSlot index={4} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                        <InputOTPSlot index={5} className="bg-[#0A0A0C] border-[#1F1F1F] text-white" />
                      </InputOTPGroup>
                    </InputOTP>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSetupMode(false);
                          setQrCode(null);
                          setSecret(null);
                          setOtpCode("");
                        }}
                        className="px-6 py-3 rounded-lg border border-[#1F1F1F] text-[#A1A1AA] hover:text-white transition-colors"
                      >
                        Отмена
                      </button>
                      <button
                        onClick={handleVerify2FA}
                        disabled={twoFALoading || otpCode.length !== 6}
                        className="flex items-center gap-2 bg-[#FF8A1E] hover:bg-[#E67A00] text-white font-bold px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {twoFALoading ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                        Подтвердить
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      
        {/* API Key Tab */}
        {activeTab === "api" && (
          <div className="bg-[#0A0A0C] border border-[#1F1F1F] rounded-lg p-6" data-testid="api-key-section">
            <h2 className="font-bold text-lg text-white mb-6">Подключение API</h2>

            <div className="space-y-6">
              {/* Status indicator */}
              <div className="flex items-center gap-3 p-4 rounded-lg border border-[#1F1F1F] bg-[#000000]">
                <div className={`w-3 h-3 rounded-full ${apiKeyActive ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-[#444]'}`}></div>
                <span className="text-sm text-white">
                  {apiKeyActive ? 'Инструмент Web-3 подключен' : 'Инструмент не подключен'}
                </span>
                {apiKeyActive && <Wifi size={16} className="text-green-500 ml-auto" />}
              </div>

              {/* Show saved key if active */}
              {apiKeyActive && (
                <div className="p-4 rounded-lg border border-green-500/20 bg-green-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Check size={16} className="text-green-500" />
                    <span className="text-sm font-medium text-green-400">API ключ активен</span>
                  </div>
                  <p className="text-xs text-[#A1A1AA] font-mono">{savedApiKey.slice(0, 8)}...{savedApiKey.slice(-4)}</p>
                  <button
                    onClick={() => {
                      localStorage.removeItem(`api_key_${user.id}`);
                      setApiKeyActive(false);
                      setSavedApiKey("");
                      setApiKey("");
                      toast.info("Ключ отключен");
                    }}
                    className="mt-3 text-xs text-[#F87171] hover:underline"
                    data-testid="disconnect-api-btn"
                  >
                    Отключить
                  </button>
                </div>
              )}

              {/* Input field */}
              {!apiKeyActive && (
                <>
                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">API Ключ</label>
                    <input
                      type="text"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Вставьте API ключ вашей биржи"
                      className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-white"
                      data-testid="api-key-input"
                    />
                    <p className="text-xs text-[#71717A] mt-2">Скопируйте ключ из настроек вашей биржи</p>
                  </div>

                  <button
                    onClick={async () => {
                      if (!apiKey.trim()) { toast.error("Введите API ключ"); return; }
                      setApiKeyLoading(true);
                      // Simulate verification delay
                      await new Promise(r => setTimeout(r, 2000));
                      localStorage.setItem(`api_key_${user.id}`, apiKey);
                      setSavedApiKey(apiKey);
                      setApiKeyActive(true);
                      setApiKeyLoading(false);
                      toast.success("Инструмент Web-3 успешно привязан к вашей бирже!");
                    }}
                    disabled={!apiKey.trim() || apiKeyLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-white transition-all disabled:opacity-40"
                    style={{
                      background: apiKey.trim() ? 'linear-gradient(135deg, #FF8A1E, #E67A00)' : 'rgba(255,255,255,0.04)',
                      boxShadow: apiKey.trim() ? 'none' : 'none',
                    }}
                    data-testid="connect-api-btn"
                  >
                    {apiKeyLoading ? (
                      <><Loader2 size={18} className="animate-spin" /> Подключение...</>
                    ) : (
                      <><Key size={18} /> Подключить инструмент</>
                    )}
                  </button>
                </>
              )}

              <p className="text-xs text-[#52525B] leading-relaxed">
                Ваш API ключ используется для связи с инструментами Web-3. Данные передаются по защищённому каналу.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Disable 2FA Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0C] rounded-lg border border-[#1F1F1F] p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Отключить 2FA</h3>
              <button
                onClick={() => {
                  setShowDisableModal(false);
                  setDisablePassword("");
                }}
                className="text-[#A1A1AA] hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-[#A1A1AA] text-sm mb-6">
              Для отключения двухфакторной аутентификации введите ваш пароль
            </p>

            <div className="mb-6">
              <label className="block text-[#A1A1AA] text-sm mb-2">Пароль</label>
              <input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white"
                placeholder="••••••••"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisableModal(false);
                  setDisablePassword("");
                }}
                className="flex-1 px-6 py-3 rounded-lg border border-[#1F1F1F] text-[#A1A1AA] hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleDisable2FA}
                disabled={twoFALoading || !disablePassword}
                className="flex-1 flex items-center justify-center gap-2 bg-[#F87171] hover:bg-[#C53030] text-white font-bold px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                {twoFALoading ? <Loader2 size={18} className="animate-spin" /> : null}
                Отключить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
